import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { ArrowButton } from '../ui/ArrowButton';
import { SplitText } from '../ui/SplitText';
import { images } from '../../data/images';
import { EASE_OUT } from '../../utils/motion';
export function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.14]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  return <section className="bg-ink px-3 pb-3 pt-[76px] sm:px-5 sm:pb-5" data-id="element-139" data-mp="21ppe38be07ep">
      <div ref={ref} className="relative isolate flex h-[calc(100svh-96px)] max-h-[940px] min-h-[660px] items-end overflow-hidden rounded-[28px] lg:items-center" data-id="element-140" data-mp="25qhnq2o68ebi">
        <motion.img src={images.hero} alt="Consultante achats et supply chain dans un entrepôt logistique" style={{
        y: imageY,
        scale: imageScale
      }} className="absolute inset-0 -z-20 h-full w-full object-cover object-[72%_center]" data-id="element-141" data-mp="25ffeqfp9g85i" />
        <div className="absolute inset-0 -z-10 bg-ink/55" data-id="element-142" data-mp="2f7bhw567eogl" />
        <div className="absolute inset-y-0 left-0 -z-10 w-full bg-ink/30 lg:w-[62%]" data-id="element-143" data-mp="1rg61nowcpu5r" />

        <motion.div style={{
        y: contentY,
        opacity: contentOpacity
      }} className="w-full px-6 pb-12 pt-24 sm:px-12 lg:px-16 lg:pb-0 lg:pt-0" data-id="element-144" data-mp="1mhcx4lz1ldhq">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT
        }} className="inline-flex items-center gap-2 rounded-md bg-white/15 px-3 py-1.5 text-sm text-white backdrop-blur-sm" data-id="element-145" data-mp="1wer0pkzva7sm">
            <span className="h-1.5 w-1.5 rounded-full bg-brass" aria-hidden="true" data-id="element-146" data-mp="3exch9guz1lxo" />
            Conseil en achats & supply chain · +20 ans d’expérience
          </motion.span>

          <h1 className="mt-7 max-w-[15ch] text-[clamp(2.6rem,6.2vw,5.5rem)] font-medium leading-[1.02] tracking-[-0.035em] text-white" data-id="element-147" data-mp="3ov0mx1u48dum">
            <SplitText text="Des achats plus performants, une supply chain plus solide." delay={0.1} data-id="element-148" data-mp="3tt25llnmqb5k" />
          </h1>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT,
          delay: 0.55
        }} className="mt-7 max-w-xl text-lg leading-relaxed text-white/80" data-id="element-149" data-mp="2w7we0lrwjfdk">
            J’aide les PME et ETI à réduire leurs coûts d’achats, à fiabiliser leurs approvisionnements et à
            structurer une fonction achats qui crée de la valeur.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT,
          delay: 0.65
        }} className="mt-10 flex flex-wrap gap-3" data-id="element-150" data-mp="2ieesivfnnskw">
            <ArrowButton to="/contact" variant="light" data-id="element-151" data-mp="vj06rugnxegs">
              Réserver un diagnostic
            </ArrowButton>
            <ArrowButton to="/services" variant="outline" data-id="element-152" data-mp="qkyp9dyvxn7f">
              Découvrir les services
            </ArrowButton>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: EASE_OUT,
        delay: 0.85
      }} className="absolute bottom-6 right-6 hidden w-[410px] md:block" data-id="element-153" data-mp="glracmci5ki7">
          <motion.div animate={{
          y: [0, -6, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="flex gap-4 rounded-2xl bg-white p-3 pr-4 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)]" data-id="element-154" data-mp="2um95fthkk5r">
            <img src={images.portrait} alt="Portrait de la fondatrice de L Procure Consulting" className="h-[104px] w-[104px] shrink-0 rounded-xl object-cover object-top" data-id="element-155" data-mp="1sxmsokkrlq73" />
            <div className="flex min-w-0 flex-1 flex-col" data-id="element-156" data-mp="1j8le8ytvbgv8">
              <p className="text-sm leading-relaxed text-ink" data-id="element-157" data-mp="1j5z730aw33dj">
                « Chaque euro bien acheté est un euro de marge retrouvé. Mon rôle : aller le chercher avec vous. »
              </p>
              <div className="mt-auto flex items-center justify-between pt-2" data-id="element-158" data-mp="1a980htypo64p">
                <span className="text-xs text-muted" data-id="element-159" data-mp="1f7jd2cge4uqm">Fondatrice · L Procure Consulting</span>
                <Link to="/a-propos" aria-label="Découvrir le parcours de la fondatrice" className="grid h-8 w-8 place-items-center rounded-lg bg-mist text-ink transition-colors duration-150 hover:bg-ink hover:text-white" data-id="element-160" data-mp="10aiky7uaexrm">
                  <ArrowUpRightIcon className="h-4 w-4" data-id="element-161" data-mp="2zljniwz7sbfn" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}